/** Contractor-wise Performance — commercial scorecard per vendor. */
import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { HardHat, Loader2, ArrowUpDown, Layers } from 'lucide-react';
import api from '@/lib/api';
import { usePanelTheme } from './panelTheme';
import ModuleDrilldownDrawer from './ModuleDrilldownDrawer';

const money = (v) => {
  const n = Number(v || 0);
  const a = Math.abs(n);
  if (a >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  if (a >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${n.toFixed(0)}`;
};

export const ContractorPerformancePanel = ({ projectId }) => {
  const T = usePanelTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('currentValue');
  const [showAll, setShowAll] = useState(false);
  const [drill, setDrill] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    setLoading(true);
    api.get(`/v1/projects/${projectId}/overview/contractor-performance`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId]);

  const BAND = useMemo(() => ({
    STRONG: T.good, ACCEPTABLE: T.accent, WATCH: T.warn, CRITICAL: T.bad,
  }), [T]);

  const rows = useMemo(() => {
    const list = [...(data?.contractors || [])];
    list.sort((a, b) => (Number(b[sortKey] ?? -1) - Number(a[sortKey] ?? -1)));
    return showAll ? list : list.slice(0, 8);
  }, [data, sortKey, showAll]);

  const t = data?.totals || {};
  const maxVal = Math.max(1, ...(data?.contractors || []).map((c) => c.currentValue || 0));

  const Head = ({ label, k, align = 'right' }) => (
    <th className={`text-${align} px-2 py-1.5 select-none ${k ? 'cursor-pointer' : ''}`}
        onClick={k ? () => setSortKey(k) : undefined}
        data-testid={k ? `contractor-sort-${k}` : undefined}>
      <span className="inline-flex items-center gap-1" style={{ color: sortKey === k ? T.accent : T.muted }}>
        {label}{k && <ArrowUpDown className="w-2.5 h-2.5" />}
      </span>
    </th>
  );

  return (
    <div className="rounded-2xl overflow-hidden" data-testid="contractor-performance-panel"
         style={{ background: T.card, border: `1px solid ${T.border}`,
                  boxShadow: T.isDark ? 'none' : '0 1px 2px rgba(15,23,42,0.04)' }}>
      <div className="px-5 pt-4 pb-3" style={{ background: T.sheen, borderBottom: `1px solid ${T.border}` }}>
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-lg grid place-items-center shrink-0" style={{ background: `${T.warn}1F` }}>
            <HardHat className="w-3.5 h-3.5" style={{ color: T.warn }} />
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold leading-tight" style={{ color: T.text }}>Contractor-wise Performance</h3>
            <p className="text-[10px]" style={{ color: T.muted }}>
              {t.vendors ?? 0} vendors · avg score {t.avgScore ?? '—'}{t.atRisk ? ` · ${t.atRisk} to watch` : ''}
            </p>
          </div>
          <button type="button" onClick={() => setDrill(true)}
            className="ml-auto shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold transition-transform hover:-translate-y-0.5"
            style={{ background: `${T.warn}18`, color: T.warn, border: `1px solid ${T.warn}44` }}
            data-testid="contractors-drilldown-btn">
            <Layers className="w-3 h-3" /> L2 · L3
          </button>
          {t.avgScore != null && (
            <div className="text-right">
              <div className="text-xl font-bold font-mono leading-none"
                   style={{ color: t.avgScore >= 85 ? T.good : t.avgScore >= 70 ? T.accent : T.warn }}>
                {t.avgScore}
              </div>
              <div className="text-[9px] uppercase tracking-wider" style={{ color: T.muted }}>avg score</div>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="h-48 flex items-center justify-center gap-2 text-xs" style={{ color: T.muted }}
             data-testid="contractor-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Scoring contractors…
        </div>
      ) : !data || (data.contractors || []).length === 0 ? (
        <div className="h-48 flex items-center justify-center text-xs" style={{ color: T.muted }}
             data-testid="contractor-empty">No contracts or invoices booked against this project yet.</div>
      ) : (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-4 gap-2" data-testid="contractor-totals">
            {[['Awarded', t.awardedValue, T.accent], ['Variations', t.variationValue, T.warn],
              ['Certified', t.certifiedValue, T.good], ['Retention', t.retentionHeld, T.violet]].map(([label, v, c], i) => (
              <motion.div key={label} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="rounded-xl px-2.5 py-2" style={{ background: T.inner, border: `1px solid ${T.border}` }}>
                <div className="text-[9px] uppercase tracking-wider" style={{ color: T.muted }}>{label}</div>
                <div className="text-sm font-mono font-semibold" style={{ color: c }}>{money(v)}</div>
              </motion.div>
            ))}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="contractor-table">
              <thead>
                <tr className="text-[9px] uppercase tracking-wider" style={{ borderBottom: `1px solid ${T.border}` }}>
                  <Head label="Contractor" align="left" />
                  <Head label="Awarded" k="currentValue" />
                  <Head label="VO %" k="variationPct" />
                  <Head label="Certified %" k="certificationPct" />
                  <Head label="On-time %" k="onTimeDeliveryPct" />
                  <Head label="Score" k="score" />
                </tr>
              </thead>
              <tbody>
                {rows.map((c, i) => (
                  <motion.tr key={c.vendorName}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i, 10) * 0.03 }}
                    style={{ borderBottom: `1px solid ${T.border}` }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = T.hover; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    data-testid={`contractor-row-${c.vendorName.replace(/\s+/g, '-').toLowerCase()}`}>
                    <td className="py-2 pr-2 max-w-[190px]">
                      <div className="truncate font-medium" style={{ color: T.text }}>{c.vendorName}</div>
                      <div className="text-[9px] truncate" style={{ color: T.muted }}>
                        {c.contracts} contract{c.contracts === 1 ? '' : 's'}
                        {c.disciplines?.length ? ` · ${c.disciplines.join(', ')}` : ''}
                      </div>
                      <div className="h-1 rounded-full mt-1 overflow-hidden" style={{ background: T.inner }}>
                        <motion.div initial={{ width: 0 }} animate={{ width: `${((c.currentValue || 0) / maxVal) * 100}%` }}
                          transition={{ duration: 0.7, ease: 'easeOut' }}
                          className="h-full rounded-full" style={{ background: BAND[c.band] || T.accent }} />
                      </div>
                    </td>
                    <td className="text-right px-2 font-mono" style={{ color: T.text2 }}>{money(c.currentValue)}</td>
                    <td className="text-right px-2 font-mono"
                        style={{ color: c.variationPct > 10 ? T.warn : T.muted }}>
                      {c.variationPct?.toFixed(1) ?? '—'}
                    </td>
                    <td className="text-right px-2 font-mono" style={{ color: T.text2 }}>{c.certificationPct ?? '—'}</td>
                    <td className="text-right px-2 font-mono"
                        style={{ color: (c.onTimeDeliveryPct ?? 100) < 80 ? T.bad : T.muted }}>
                      {c.onTimeDeliveryPct ?? '—'}
                    </td>
                    <td className="text-right px-2">
                      <span className="px-2 py-0.5 rounded-full font-mono font-semibold"
                            style={{ background: `${BAND[c.band] || T.muted}1F`, color: BAND[c.band] || T.muted }}
                            title={c.band || ''}>
                        {c.score ?? '—'}
                      </span>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {(data.contractors || []).length > 8 && (
            <button type="button" onClick={() => setShowAll((v) => !v)}
                    className="w-full text-[10px] py-1.5 rounded-lg transition-colors"
                    style={{ color: T.accent, background: T.inner, border: `1px solid ${T.border}` }}
                    data-testid="contractor-show-all">
              {showAll ? 'Show top 8 only' : `Show all ${data.contractors.length} contractors`}
            </button>
          )}
        </div>
      )}
      {drill && (
        <ModuleDrilldownDrawer projectId={projectId} module="contractors" onClose={() => setDrill(false)} />
      )}
    </div>
  );
};

export default ContractorPerformancePanel;
